import React, { useCallback, useEffect, useState } from 'react';
import styles from './EventsSection.module.css';

const API = process.env.REACT_APP_API_URL || '/api';
const TIME_ZONE = 'America/Mexico_City';
const EDUCATION_LEVELS = [
  { key: 'bachillerato', label: 'Bachillerato' },
  { key: 'tsu', label: 'TSU' },
  { key: 'licenciatura', label: 'Licenciatura' },
  { key: 'maestria', label: 'Maestría' },
  { key: 'especialidad', label: 'Especialidad' },
];
const LEVEL_LABELS = Object.fromEntries(EDUCATION_LEVELS.map(({ key, label }) => [key, label]));

const EMPTY_FORM = {
  title: '',
  description: '',
  educationLevels: [],
  startDate: '',
  startTime: '18:00',
  durationMinutes: '60',
  periodicity: 'weekly',
};

const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('odm_token')}`,
});

const formatDateTime = (value) => {
  if (!value) return '—';
  return new Intl.DateTimeFormat('es-MX', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: TIME_ZONE,
  }).format(new Date(value));
};

const toLocalFormValues = (value) => {
  if (!value) return { startDate: '', startTime: '18:00' };
  const parts = Object.fromEntries(new Intl.DateTimeFormat('en-CA', {
    timeZone: TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(new Date(value)).map(({ type, value: partValue }) => [type, partValue]));
  return {
    startDate: `${parts.year}-${parts.month}-${parts.day}`,
    startTime: `${parts.hour}:${parts.minute}`,
  };
};

const periodicityLabel = {
  weekly: 'Semanal',
  biweekly: 'Quincenal',
  monthly: 'Mensual',
};

export default function EventsSection() {
  const [events, setEvents] = useState([]);
  const [view, setView] = useState('list');
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${API}/admin/events`, { headers: authHeaders() });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'No se pudieron cargar los eventos.');
      setEvents(Array.isArray(data.data) ? data.data : []);
    } catch (requestError) {
      setError(requestError.message || 'No se pudo conectar con el servidor.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setError('');
    setNotice('');
    setView('form');
  };

  const openEdit = (event) => {
    const local = toLocalFormValues(event.startAt);
    setEditing(event);
    setForm({
      title: event.title || '',
      description: event.description || '',
      educationLevels: event.educationLevels || [],
      startDate: local.startDate,
      startTime: local.startTime,
      durationMinutes: String(event.durationMinutes || 60),
      periodicity: event.periodicity || 'weekly',
    });
    setError('');
    setNotice('');
    setView('form');
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const toggleEducationLevel = (level) => {
    setForm((current) => ({
      ...current,
      educationLevels: current.educationLevels.includes(level)
        ? current.educationLevels.filter((item) => item !== level)
        : [...current.educationLevels, level],
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    setNotice('');
    try {
      const response = await fetch(
        editing ? `${API}/admin/events/${editing._id}` : `${API}/admin/events`,
        {
          method: editing ? 'PATCH' : 'POST',
          headers: authHeaders(),
          body: JSON.stringify({
            ...form,
            durationMinutes: Number(form.durationMinutes),
            timeZone: TIME_ZONE,
          }),
        }
      );
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'No se pudo guardar el evento.');
      await fetchEvents();
      setNotice(editing ? 'Evento actualizado.' : 'Evento creado con su enlace de Google Meet.');
      setView('list');
      setEditing(null);
    } catch (requestError) {
      setError(requestError.message || 'No se pudo conectar con el servidor.');
    } finally {
      setSaving(false);
    }
  };

  const cancelEvent = async (event) => {
    if (!window.confirm(`¿Cancelar “${event.title}” y sus próximas invitaciones?`)) return;
    setError('');
    setNotice('');
    try {
      const response = await fetch(`${API}/admin/events/${event._id}/cancel`, {
        method: 'POST',
        headers: authHeaders(),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'No se pudo cancelar el evento.');
      await fetchEvents();
      setNotice(data.warning || 'Evento cancelado.');
    } catch (requestError) {
      setError(requestError.message || 'No se pudo conectar con el servidor.');
    }
  };

  const structuralFieldsLocked = Boolean(editing?.registrationCount > 0);

  if (view === 'form') {
    return (
      <section className={styles.section}>
        <div className={styles.headingRow}>
          <div>
            <p className={styles.eyebrow}>Google Calendar</p>
            <h1>{editing ? 'Editar evento' : 'Crear evento'}</h1>
          </div>
          <button type="button" className={styles.secondaryButton} onClick={() => setView('list')}>
            Volver
          </button>
        </div>

        <form className={styles.formCard} onSubmit={handleSubmit}>
          <label className={styles.fieldWide}>
            <span>Nombre del evento</span>
            <input name="title" value={form.title} onChange={handleChange} required maxLength={160} />
          </label>
          <label className={styles.fieldWide}>
            <span>Descripción</span>
            <textarea name="description" value={form.description} onChange={handleChange} rows={4} maxLength={2000} />
          </label>
          <fieldset className={styles.levelFieldset}>
            <legend>Niveles educativos de la reunión</legend>
            <p>Selecciona uno o varios niveles cuyos prospectos pueden registrarse.</p>
            <div className={styles.levelOptions}>
              {EDUCATION_LEVELS.map(({ key, label }) => (
                <label key={key} className={styles.levelOption}>
                  <input
                    type="checkbox"
                    checked={form.educationLevels.includes(key)}
                    onChange={() => toggleEducationLevel(key)}
                  />
                  <span>{label}</span>
                </label>
              ))}
            </div>
          </fieldset>

          {structuralFieldsLocked && (
            <p className={styles.lockedNotice}>
              Este evento ya tiene personas registradas. Para cambiar su programación debes cancelarlo y crear uno nuevo.
            </p>
          )}

          <div className={styles.formGrid}>
            <label>
              <span>Primera fecha</span>
              <input type="date" name="startDate" value={form.startDate} onChange={handleChange} required disabled={structuralFieldsLocked} />
            </label>
            <label>
              <span>Hora</span>
              <input type="time" name="startTime" value={form.startTime} onChange={handleChange} required disabled={structuralFieldsLocked} />
            </label>
            <label>
              <span>Duración (minutos)</span>
              <input type="number" name="durationMinutes" min="1" max="1440" value={form.durationMinutes} onChange={handleChange} required disabled={structuralFieldsLocked} />
            </label>
            <label>
              <span>Periodicidad</span>
              <select name="periodicity" value={form.periodicity} onChange={handleChange} disabled={structuralFieldsLocked}>
                <option value="weekly">Semanal</option>
                <option value="biweekly">Quincenal</option>
                <option value="monthly">Mensual</option>
              </select>
            </label>
          </div>

          <p className={styles.timeZoneNote}>Zona horaria: Ciudad de México</p>
          {error && <p className={styles.error}>{error}</p>}
          <div className={styles.actions}>
            <button type="button" className={styles.secondaryButton} onClick={() => setView('list')}>Cancelar</button>
            <button type="submit" className={styles.primaryButton} disabled={saving}>
              {saving ? 'Sincronizando con Google…' : (editing ? 'Guardar cambios' : 'Crear evento y Meet')}
            </button>
          </div>
        </form>
      </section>
    );
  }

  return (
    <section className={styles.section}>
      <div className={styles.headingRow}>
        <div>
          <p className={styles.eyebrow}>Admisiones</p>
          <h1>Eventos</h1>
          <p className={styles.subtitle}>Administra las charlas y sus enlaces de Google Meet desde ODM.</p>
        </div>
        <button type="button" className={styles.primaryButton} onClick={openCreate}>Crear evento</button>
      </div>

      {notice && <p className={styles.notice}>{notice}</p>}
      {error && <p className={styles.error}>{error}</p>}
      {loading ? (
        <p className={styles.empty}>Cargando eventos…</p>
      ) : events.length === 0 ? (
        <div className={styles.emptyCard}>
          <h2>Aún no hay eventos</h2>
          <p>Crea la primera charla para mostrarla en el formulario público.</p>
        </div>
      ) : (
        <div className={styles.cards}>
          {events.map((event) => (
            <article key={event._id} className={`${styles.card} ${event.status === 'cancelled' ? styles.cardCancelled : ''}`}>
              <div className={styles.cardHeader}>
                <div>
                  <span className={`${styles.status} ${event.status === 'active' ? styles.statusActive : styles.statusCancelled}`}>
                    {event.status === 'active' ? 'Activo' : 'Cancelado'}
                  </span>
                  <h2>{event.title}</h2>
                </div>
                <span className={styles.registrationCount}>{event.registrationCount} registrados</span>
              </div>
              {event.description && <p className={styles.description}>{event.description}</p>}
              <div className={styles.levelTags} aria-label="Niveles educativos">
                {(event.educationLevels || []).length > 0
                  ? event.educationLevels.map((level) => (
                    <span key={level}>{LEVEL_LABELS[level] || level}</span>
                  ))
                  : <span>Sin nivel asignado</span>}
              </div>
              <dl className={styles.details}>
                <div><dt>Próxima sesión</dt><dd>{event.status === 'active' ? formatDateTime(event.nextOccurrenceAt || event.startAt) : '—'}</dd></div>
                <div><dt>Periodicidad</dt><dd>{periodicityLabel[event.periodicity] || event.periodicity}</dd></div>
                <div><dt>Duración</dt><dd>{event.durationMinutes} minutos</dd></div>
                <div><dt>Sincronización</dt><dd>{event.syncError || (event.lastSyncedAt ? 'Correcta' : 'Pendiente')}</dd></div>
              </dl>
              <div className={styles.cardActions}>
                {event.googleMeetUrl && event.status === 'active' && (
                  <a className={styles.linkButton} href={event.googleMeetUrl} target="_blank" rel="noreferrer">Abrir Meet</a>
                )}
                {event.status === 'active' && (
                  <>
                    <button type="button" className={styles.secondaryButton} onClick={() => openEdit(event)}>Editar</button>
                    <button type="button" className={styles.dangerButton} onClick={() => cancelEvent(event)}>Cancelar evento</button>
                  </>
                )}
              </div>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}
